import {
  HighlightData,
  HighlightRawData,
  HighlightsFilterType,
  HighlightsState,
} from "./types";
import { createSelector } from "@reduxjs/toolkit";
import { getPageBreaks } from "../pagebreaks/selectors";
import {
  findPageLabelFromCfi,
  safeParseCfiRange,
} from "../../../../../../common/utils/cfi";
import {
  getEmojiMarkers,
  getHighlightsSubscribedMarkers,
  getMarkers,
} from "../markers/selectors";
import { CFI, parseCfi, parseCfiRange } from "epubjs";
import { getCurrentPageCfi, isPageLoading } from "../nav/load-status/selectors";
import { isEmoji, sortHighlights } from "./utils";
import { getTocNodesWithValidCFIs, getTocTitleCount } from "../toc/selectors";
import { generateUUID } from "../../../../../../common/utils/uuid";
import { getChapterNodeForCFI } from "../toc/utils";
import { ChapterHighlights } from "../../../../ui/default/features/workbook/highlights/WorkbookHighlights";

function getBase(state): HighlightsState {
  return state.highlights;
}

const getMyHighlightsRawData = (state) => getBase(state).highlightsRawData;

export const getSubscribedHighlightsRawData = (state) =>
  getBase(state).subscribedHighlightsRawData;

const getAllHighlightsRawData = createSelector(
  getMyHighlightsRawData,
  getSubscribedHighlightsRawData,
  (myHighlights, subscribeHighlights) =>
    myHighlights.concat(subscribeHighlights)
);

export const getHighlightsLoadStatus = (state) => getBase(state).loadStatus;

export const getReconstructedHighlightsResponse = createSelector(
  (state) => getMyHighlightsRawData(state),
  (state) => getSubscribedHighlightsRawData(state),
  (state) => getMarkers(state),
  (state) => getHighlightsSubscribedMarkers(state),
  (myHighlights, subscribedHighlights, myMarkers, subscribedMarkers) => {
    return {
      highlights: myHighlights,
      subscribedHighlights,
      markers: myMarkers,
      subscribedMarkers,
    };
  }
);

export const getHighlightsData = createSelector(
  getAllHighlightsRawData,
  getPageBreaks,
  getMarkers,
  getEmojiMarkers,
  getHighlightsSubscribedMarkers,
  (highlightsRawData, pageBreaks, markers, emojiMarkers, subscribedMarkers) => {
    const emojiMarkerIds = emojiMarkers.map(
      (emojiMarker) => emojiMarker.globalId
    ); // in case a user has more than one emoji marker
    if (!highlightsRawData || !pageBreaks || !markers || !subscribedMarkers) {
      return undefined;
    }
    const highlightsData = highlightsRawData?.map<HighlightData>(
      (highlightRawData) => ({
        ...highlightRawData,
        startLabel: findStartLabel(highlightRawData, pageBreaks),
        authorInitials: extractAuthorInitials(highlightRawData.authorName),
        isEmoji:
          isEmoji(highlightRawData) &&
          emojiMarkerIds.includes(highlightRawData.markerId),
        markerColorName:
          markers
            .concat(subscribedMarkers)
            .find((marker) => marker.globalId === highlightRawData.markerId)
            ?.label ?? "",
      })
    );

    return sortHighlights(highlightsData);
  }
);

export const getHighlightsFilter = (state) => getBase(state).highlightsFilter;

export const getFilteredHighlights = createSelector(
  getHighlightsData,
  getHighlightsFilter,
  (highlights, filter) => {
    return highlights?.filter(
      (highlight) =>
        filter.type === HighlightsFilterType.None ||
        (filter.type === HighlightsFilterType.OnlyNotes &&
          highlight.hasNote &&
          highlight.markerColorName) ||
        (filter.type === HighlightsFilterType.Marker &&
          highlight.markerId === filter.globalId)
    );
  }
);

const findStartLabel = (highlight: HighlightRawData, pageBreaks): string => {
  let cfiStart: string = "";
  if (highlight.cfi) {
    const range = safeParseCfiRange(highlight.cfi);
    if (!range) {
      return "";
    }
    cfiStart = range.start.toString();
  }
  if (!highlight.startLabel && cfiStart) {
    return findPageLabelFromCfi(pageBreaks, cfiStart);
  }
  return highlight.startLabel;
};

const extractAuthorInitials = (
  authorName: string | undefined
): string | undefined => {
  if (!authorName) {
    return undefined;
  }
  return authorName
    .split(" ")
    .reduce(
      (prev, cur, i, parts) =>
        i === 0 || i === parts.length - 1
          ? prev + cur.toUpperCase().charAt(0)
          : prev,
      ""
    );
};

export const getHighlightsForCurrentDocument = createSelector(
  (state) => getHighlightsData(state),
  (state) => getCurrentPageCfi(state),
  (state) => isPageLoading(state),
  (allHighlights, currentCfi, isLoading) => {
    if (!currentCfi || !allHighlights || isLoading) {
      return [];
    }
    const pageCfi = parseCfi(currentCfi);
    return allHighlights.filter((highlight) => {
      let cfiToUse;
      try {
        if (highlight.cfi) {
          cfiToUse = parseCfiRange(highlight.cfi).start.contentDoc;
        } else {
          cfiToUse = parseCfi(`/${highlight.startPage}`);
        }
        return CFI.compare(pageCfi, cfiToUse) === 0;
      } catch {
        return false;
      }
    });
  }
);

export const getHighlightForId = (highlightId) =>
  createSelector(getHighlightsData, (highlights) => {
    if (highlights) {
      if (!highlightId) {
        return null;
      }
      return highlights.find((h) => h.syncId === highlightId);
    } else {
      return null;
    }
  });

export const getHighlightMarker = (highlightId) =>
  createSelector(
    getMarkers,
    getHighlightForId(highlightId),
    (markers, highlight) => {
      if (!highlight) {
        return null;
      }

      return markers.find((m) => m.globalId === highlight.markerId);
    }
  );
const createGroup = (title, uuid, highlights: HighlightData[] = []) => ({
  title,
  uuid: uuid || `${title}_${generateUUID()}`,
  highlights,
});

export const getFilteredHighlightsByChapter = createSelector(
  getFilteredHighlights,
  getTocTitleCount,
  getTocNodesWithValidCFIs,
  (filteredHighlights, chapterTitleCount, tocNodesWithValidCFIS) => {
    let prevUuid;
    return filteredHighlights?.reduce<ChapterHighlights>(
      (result, highlight, i, values) => {
        const chapterTitle = highlight.chapterTitle;
        let lastGroup = result[result.length - 1];
        if (chapterTitleCount[chapterTitle] > 1) {
          // Find TOC node for highlight if chapter title exists multiple times
          let uuid;
          const safeRange = safeParseCfiRange(highlight.cfi);
          if (safeRange) {
            uuid = getChapterNodeForCFI(safeRange.start.toString(), {
              toc: tocNodesWithValidCFIS,
            })?.uuid;
          }

          const lastHighlight =
            lastGroup?.highlights[lastGroup.highlights.length - 1];

          // Check if node uuid is the same as last checked uuid
          if (!lastHighlight || uuid === prevUuid) {
            if (!lastGroup || lastGroup.title !== highlight.chapterTitle) {
              lastGroup = createGroup(chapterTitle, uuid);
              result.push(lastGroup);
            }
            lastGroup.highlights.push(highlight);
          } else {
            result.push(createGroup(chapterTitle, uuid, [highlight]));
          }
          prevUuid = uuid;
        } else if (i > 0 && values[i - 1].chapterTitle !== chapterTitle) {
          // If chapter has no duplicate value, and does not match the chapter of the previous highlight, create a group
          result.push(createGroup(chapterTitle, chapterTitle, [highlight]));
        } else {
          // Append to the last group
          if (!lastGroup) {
            lastGroup = createGroup(chapterTitle, chapterTitle);
            result.push(lastGroup);
          }
          lastGroup.highlights.push(highlight);
        }
        return result;
      },
      []
    );
  }
);
